import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';
import { unsubscribePush } from '../utils/webpush';
import { stopNativePush } from '../utils/nativePush';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try { setUser(JSON.parse(savedUser)); } catch (e) { localStorage.clear(); }
    }
    if (!token) { setLoading(false); return undefined; }
    // /uploads 里的图片要登录才能看，<img> 带不了 Authorization 头，靠服务端下发的 Cookie。
    // 升级前登录的老会话没有这个 Cookie：启动时补发一次，并等它回来再渲染页面，否则首屏的图片会加载失败。
    // validateStatus 全放行 = 失败（过期 / 断网）也静默，不弹“登录已过期”；最多等 2 秒，之后的真实请求自己会处理 401。
    api.post('/auth/session', null, { timeout: 2000, validateStatus: () => true })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const login = async (identifier, password, code) => {
    const res = await api.post('/auth/login', { identifier, password, code });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    // 退出前先取消 Web Push 订阅，避免旧账号在该设备上继续收到推送
    await unsubscribePush();
    await stopNativePush(); // 停止安卓前台推送服务并注销设备令牌
    // 清除 /uploads 图片凭证 Cookie（HttpOnly，只能由服务端清）；失败不影响退出
    try { await api.post('/auth/logout', null, { timeout: 2000, validateStatus: () => true }); } catch (e) { /* ignore */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = user?.role === 'admin';
  const isAgent = user?.role === 'agent';
  const isAdminOrAgent = isAdmin || isAgent;
  const canManageImages = isAdmin || !!user?.can_manage_images;
  const canSendPush = isAdmin || !!user?.can_send_push;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isAgent, isAdminOrAgent, canManageImages, canSendPush }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
